import * as React from 'react';
import { renderToString } from 'react-dom/server';

import EmptyState from '../empty_state'
import { accountGreenImg, formatDateToMMDDYY, STARTER, INTERMEDIATE, ADVANCED, helpImg, TABLE_WIDTH_PROPS, } from '../shared'
import { SequencePracticeInterface, StudentHistoryInterface, StudentLearningSequenceInterface, } from '../interfaces'
import { Spinner, DataTable, Tooltip, DataTableChip, } from '../../../../../Shared/index'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'

export const DIAGNOSTIC_INSIGHTS_IMG_SRC = `${window.postbuildEnv.CDN_URL}/images/pages/diagnostic_insights`
export const FILLED_BLUE_DIAMOND_WITH_CHECKMARK_ALT_TEXT = "Filled blue diamond with checkmark, indicating that the student has completed both the pre and post diagnostic and demonstrated full proficiency"
export const FILLED_BLUE_DIAMOND_ALT_TEXT = "Filled blue diamond with checkmark, indicating that the student has completed both the pre and post diagnostic but did not demonstrate full proficiency"
export const OUTLINED_BLUE_DIAMOND_ALT_TEXT = "Outlined blue diamond, indicating that the student has completed the pre diagnostic but did not demonstrate proficiency"
export const OUTLINED_BLUE_DIAMOND_WITH_CHECKMARK_ALT_TEXT = "Outlined blue diamond, indicating that the student has completed both the pre diagnostic and demonstrated full proficiency"

const filledBlueDiamondImg = <img alt={FILLED_BLUE_DIAMOND_ALT_TEXT} src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/filled-blue-diamond.svg`} />
const filledBlueDiamondWithCheckMarkImg = <img alt={FILLED_BLUE_DIAMOND_WITH_CHECKMARK_ALT_TEXT} src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/filled-blue-diamond-with-checkmark.svg`} />
const outlinedBlueDiamondImg = <img alt={OUTLINED_BLUE_DIAMOND_ALT_TEXT} src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/outlined-blue-diamond.svg`} />
const outlinedBlueDiamondWithCheckMarkImg = <img alt={OUTLINED_BLUE_DIAMOND_WITH_CHECKMARK_ALT_TEXT} src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/outlined-blue-diamond-with-checkmark.svg`} />

interface HistoryProps {
  sequencePractice: SequencePracticeInterface;
  showEmptyState: boolean;
  studentHistories: StudentHistoryInterface[];
  useELLDiagnostics: boolean;
  teacherClassroomIds: number[];
}

const History = ({ sequencePractice, studentHistories, useELLDiagnostics, teacherClassroomIds, showEmptyState, }: HistoryProps) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 351 },
      { width: 277, minWidth: 230 },
      { width: 277, minWidth: 230 },
      { width: 277, minWidth: 230 },
    ],
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });

  const headers = (ell) => {
    const ellString = ell ? 'ELL ' : ''
    return [
      {
        name: 'Student',
        width: columnWidths['351w'],
        attribute: 'student',
        isSortable: true,
        sortAttribute: 'nameForSort'
      },
      {
        name: `${ellString}Starter Diagnostic`,
        width: columnWidths['277w-230min'],
        attribute: STARTER,
        noTooltip: true
      },
      {
        name: `${ellString}Intermediate Diagnostic`,
        width: columnWidths['277w-230min'],
        attribute: INTERMEDIATE,
        noTooltip: true
      },
      {
        name: `${ellString}Advanced Diagnostic`,
        width: columnWidths['277w-230min'],
        attribute: ADVANCED,
        noTooltip: true
      },
    ]
  }

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: !!studentHistories,
    dependencies: [Object.values(columnWidths).join('|')], // ensure resync when columnWidths change
  });

  if (!studentHistories) {
    return (
      <div className="diagnostic-insights-content history by-class">
        <Spinner />
      </div>
    )
  }

  if (showEmptyState) {
    return (
      <EmptyState
        bodyText="There is no history available for the filters selected. Consider modifying a filter or assigning a diagnostic to your students. Once they complete assessments, this report will become available."
        headerText="No diagnostic history is available for this class"
      />
    )
  }

  const renderDiagnosticSquares = (studentLearningSequences: StudentLearningSequenceInterface[]) => {
    if (!studentLearningSequences) { return null }

    return studentLearningSequences.map(studentLearningSequence => {
      const { student_learning_sequence_activities, classroom_id, id, } = studentLearningSequence
      const { pre_diagnostic, post_diagnostic, } = student_learning_sequence_activities

      const { pre_practice, post_practice, } = sequencePractice[id] || {}

      // sometimes there is a studentLearningSequence when a student has completed an activity that belongs to the recommendation sequence,
      // even if they didn't complete either of the diagnostics, in which case we don't want to render anything
      if (!pre_diagnostic) { return null }

      let icon: JSX.Element

      if (pre_diagnostic && post_diagnostic) {
        icon = pre_diagnostic[0].proficient || post_diagnostic[0].proficient ? filledBlueDiamondWithCheckMarkImg : filledBlueDiamondImg
      } else {
        icon = pre_diagnostic[0].proficient ? outlinedBlueDiamondWithCheckMarkImg : outlinedBlueDiamondImg
      }

      const tooltipText = [<p key="assigner"><strong>{teacherClassroomIds.includes(classroom_id) ? 'Assigned by you' : 'Assigned by another teacher'}</strong></p>, <br key="break" />]

      // Baseline Pre Completed
      if (pre_diagnostic?.length) {
        const preDiagnosticCompletionDate = formatDateToMMDDYY(pre_diagnostic[pre_diagnostic.length - 1].completed_at);
        tooltipText.push(
          <p key="baseline-pre">
            <strong>Baseline Pre Completed:</strong> {preDiagnosticCompletionDate}
          </p>
        );
      }

      // Baseline Pre Practice
      if (pre_practice) {
        const firstPracticeDate = formatDateToMMDDYY(pre_practice.earliest)
        const lastPracticeDate = formatDateToMMDDYY(pre_practice.latest)
        tooltipText.push(
          <p key="baseline-pre-practice">
            <strong>Baseline Pre Practice:</strong> {pre_practice.percentages.length} activities (
            {firstPracticeDate} - {lastPracticeDate})
          </p>
        );
      }

      // Growth Post Completed
      if (post_diagnostic?.length) {
        const postDiagnosticCompletionDate = formatDateToMMDDYY(post_diagnostic[post_diagnostic.length - 1].completed_at)
        tooltipText.push(
          <p key="growth-post">
            <strong>Growth Post Completed:</strong> {postDiagnosticCompletionDate}
          </p>
        );
      }

      // Growth Post Practice
      if (post_practice) {
        const firstPostPracticeDate = formatDateToMMDDYY(post_practice.earliest);
        const lastPostPracticeDate = formatDateToMMDDYY(post_practice.latest);
        tooltipText.push(
          <p key="growth-post-practice">
            <strong>Growth Post Practice:</strong> {post_practice.percentages.length} activities (
            {firstPostPracticeDate} - {lastPostPracticeDate})
          </p>
        );
      }

      return (
        <Tooltip
          key={id}
          skipPositioning={true}
          tooltipText={renderToString(tooltipText)}
          tooltipTriggerText={icon}
        />
      )
    }).filter(Boolean)
  }

  const rows = studentHistories.map(studentHistory => {
    const { user_id, user_name, student_learning_sequences, } = studentHistory
    const { starter, intermediate, advanced, } = student_learning_sequences
    const nameAsArray = user_name.split(' ')
    const nameForSort = `${nameAsArray[nameAsArray.length - 1]} ${nameAsArray[0]}`
    const byStudentBaseUrl = `${window.location.href.split('by_class')[0]}by_student`
    return {
      id: user_id,
      student: <DataTableChip color="green" icon={accountGreenImg} label={user_name} link={`${byStudentBaseUrl}/${user_id}`} />,
      nameForSort,
      starter: renderDiagnosticSquares(starter),
      intermediate: renderDiagnosticSquares(intermediate),
      advanced: renderDiagnosticSquares(advanced)
    }
  })

  return (
    <section className="diagnostic-insights-content history by-class" ref={containerRef}>
      <section className="key">
        <div className="key-section">
          <div className="image-and-text">
            {outlinedBlueDiamondImg}
            <p>Completed <span>Pre Diagnostic</span></p>
          </div>
        </div>
        <div className="key-section">
          <div className="image-and-text">
            {filledBlueDiamondImg}
            <p>Completed <span>Pre and Post Diagnostic</span></p>
          </div>
        </div>
        <div className="key-section">
          <div className="image-and-text">
            <div>
              {outlinedBlueDiamondWithCheckMarkImg}
              {filledBlueDiamondWithCheckMarkImg}
            </div>
            <p>
              Demonstrated
              <span>Full proficiency in most skills</span>
            </p>
          </div>
          <Tooltip
            tooltipText="Full proficiency in most skills means demonstrating full proficiency (answering all questions correctly) in at least 80% of the skills on a diagnostic. For example, on a 5-skill diagnostic, this means demonstrating full proficiency in at least 4 skills."
            tooltipTriggerText={helpImg}
          />
        </div>
      </section>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers(useELLDiagnostics)}
          reportingFormat={true}
          rows={rows}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}

export default History
